import dayjs from 'dayjs';

import { Audit } from '@graphql/generated';

/** Format a date as "Jan 6, 2026" */
export const formatActivityDateHeader = (date: Date): string =>
  dayjs(date).format('MMM D, YYYY');

/** Derive a YYYY-MM-DD bucket string from a date for grouping */
const dateBucket = (date: Date): string => dayjs(date).format('YYYY-MM-DD');

// ── Typed row union ────────────────────────────────────────────────────────
export type DateHeaderRow = { type: 'header'; dateKey: string; label: string };
export type AuditRow = {
  type: 'item';
  audit: Audit;
  indexInGroup: number;
  isLastInGroup: boolean;
};
export type ActivityRow = DateHeaderRow | AuditRow;

/**
 * Build the interleaved header + item row array from a sorted audit list.
 * Marks each AuditRow with isLastInGroup so the item can omit its bottom
 * separator when it is the final entry in its date group.
 */
export const buildActivityRows = (audits: Audit[]): ActivityRow[] => {
  const rows: ActivityRow[] = [];
  let lastBucket = '';
  audits.forEach((audit, i) => {
    const bucket = dateBucket(new Date(audit.createdAt));
    if (bucket !== lastBucket) {
      rows.push({
        type: 'header',
        dateKey: bucket,
        label: formatActivityDateHeader(new Date(audit.createdAt)),
      });
      lastBucket = bucket;
    }
    const nextBucket =
      i + 1 < audits.length
        ? dateBucket(new Date(audits[i + 1].createdAt))
        : null;
    rows.push({
      type: 'item',
      audit,
      indexInGroup: rows.length,
      isLastInGroup: nextBucket !== bucket,
    });
  });
  return rows;
};

// ── globalActivitySearch pagination ────────────────────────────────────────
/**
 * Resolve the next `from` offset for a `globalActivitySearch` page.
 *
 * `from`/`size` are raw Elasticsearch audit offsets, but the resolver collapses
 * document-upload audits that share a request_uuid into a single grouped entry
 * before returning, so a page of `size` raw audits can come back as fewer rows.
 * Advancing by the returned row count therefore rewound the cursor into audits
 * page N already consumed, and the re-formed group carried a different leading
 * audit id — so the id-based append filter could not spot it and the same
 * upload showed up twice in a row (TA-10510).
 *
 * Always advance by the requested page size, and decide "has more" from the
 * raw total against that same raw offset. Takes the returned page itself
 * (every row carries the same raw `total`) so each caller stays a single
 * statement.
 *
 * @param from Raw offset the page just fetched started at
 * @param pageSize Raw page size requested (the `size` argument)
 * @param page Rows the resolver returned for that request
 */
export const getNextActivityPage = (
  from: number,
  pageSize: number,
  page: readonly { total: number }[] | null | undefined
): { nextFrom: number; canLoadMore: boolean } => {
  const total = page && page.length > 0 ? page[0].total : 0;
  const nextFrom = from + pageSize;
  return total > nextFrom
    ? { nextFrom, canLoadMore: true }
    : { nextFrom: 0, canLoadMore: false };
};
